import {
  Box,
  Button,
  Card,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  closeIcon,
  courseListSecondaryIcon,
  filterLightIcon,
  filterPrimaryIcon,
  listCheckIcon,
} from "assets/images";
import NoRecords from "components/NoRecords";
import CustomPagination from "components/customPagination";
import dayjs from "dayjs";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CompletedSLPListPayload,
  CompletedSLPListPDFPayload,
  CompletedSLPListPDFSuccessPayload,
  CompletedSLPListSuccessPayload,
  PLAUserListPayload,
} from "store/slpDashboard/type";
import {
  COMPLETED_TO_DATE_SHOULD_GREATER_MSG,
  NO_RECORD_FOUND,
} from "utils/constants/Messages";
import {
  CLEAR_TOOLTIP_TITLE,
  DATE_ONLY_FORMAT,
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import { DEFAULT_SLP_REQUESTS_FILTER } from "utils/constants/slp";
import { AppRoutings } from "utils/enums/app-routings";
import { SLPFormStep } from "utils/enums/slp-form";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  allowSearch,
  checkEndDateLessThanStart,
  getPagePayloadValues,
  onTextFieldKeyPress,
  printPDFHelper,
  showLoader,
  toggleFilter,
} from "utils/helper";
import { getSLPStatusClass, getSLPStatusText } from "utils/helper/slp";
import {
  ICompletedSLPRequest,
  IEnhancedTablePropsForSLPCompleted,
  ISLPCompletedKeys,
  ISLPDashboardListMeta,
  ISLPRequestsFilter,
  ISLPRequestsPayloadValues,
} from "utils/interfaces/slp";
import SLPRequestsFilter from "../SLPRequestsFilter";

const EnhancedTableHead = (props: IEnhancedTablePropsForSLPCompleted) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof ISLPCompletedKeys) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

export type SLPRequestProps = {
  completedSLPListRequest: (payload: CompletedSLPListPayload) => {};
  completedSLPListPDFRequest: (payload: CompletedSLPListPDFPayload) => {};
  plaUserListRequest: (payload: PLAUserListPayload) => {};
  userId: number;
};

const CompletedSLPs: React.FC<SLPRequestProps> = (props) => {
  const { plaUserListRequest, userId } = props;
  const history = useHistory();

  const [completedSLPs, setCompletedSLPs] = useState<
    Array<ICompletedSLPRequest>
  >([]);
  const [completedSLPsMeta, setCompletedSLPsMeta] =
    useState<ISLPDashboardListMeta>();
  const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [searchText, setSearchText] = useState<string>("");
  const [order, setOrder] = useState<Order>(OrderByOptions.Desc);
  const [orderBy, setOrderBy] =
    useState<keyof ISLPCompletedKeys>("dateCompleted");
  const [filterBox, setFilterBox] = useState<boolean>(false);
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
  const [exportMenu, setExportMenu] = useState<null | HTMLElement>(null);

  const firstRenderFilter: ISLPRequestsFilter = useMemo(() => {
    return {
      ...DEFAULT_SLP_REQUESTS_FILTER,
      allocatedTo: [userId],
    };
  }, [userId]);

  const [filteredData, setFilteredData] =
    useState<ISLPRequestsFilter>(firstRenderFilter);

  const exportOpen = Boolean(exportMenu);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenu(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportMenu(null);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const completedSLPListSuccessCallback = (
    data: CompletedSLPListSuccessPayload
  ) => {
    setCompletedSLPs(data.completedSLPs);
    setCompletedSLPsMeta(data.completedSLPsPagination);
  };

  const getSLPRequests = (
    filterData: ISLPRequestsFilter,
    filtered?: boolean,
    pageNum?: number
  ) => {
    const { completedSLPListRequest } = props;
    if (completedSLPListRequest) {
      showLoader();
      const values: ISLPRequestsPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.search = filtered ? "" : searchText.trim();

      if (filterData.allocatedTo) values.allocatedTo = filterData.allocatedTo;
      if (filterData.numberOfCompletedSLP !== null)
        values.numberOfCompletedSLP = filterData.numberOfCompletedSLP;
      if (filterData.from)
        values.from = dayjs(filterData.from).format(DATE_ONLY_FORMAT);
      if (filterData.to)
        values.to = dayjs(filterData.to).format(DATE_ONLY_FORMAT);
      if (filterData.slpParticipation)
        values.slpParticipation = filterData.slpParticipation;

      if (filterData) {
        setFilteredData(filterData);
        setAppliedFilter(true);

        if (pageNum) {
          values.pageNo = pageNum;
          setPageNo(pageNum);
        }
      } else {
        setFilteredData(DEFAULT_SLP_REQUESTS_FILTER);
      }

      const payload = {
        values,
        callback: completedSLPListSuccessCallback,
      };
      completedSLPListRequest(payload);
    }
  };

  const getSLPRequestsModifiedData = (list: Array<ICompletedSLPRequest>) => {
    const data = list?.map((slp: ICompletedSLPRequest) => {
      return {
        "SLP Number": slp.slpNumber,
        "Student Name": slp.studentName,
        "SLP Status": getSLPStatusText(slp.status),
        "Date of Request": moment(slp.dateOfRequest).format(DATE_VIEW_FORMAT),
        "Date Completed": moment(slp.dateCompleted).format(DATE_VIEW_FORMAT),
        "Allocated To": slp.allocatedToName ?? "-",
      };
    });

    return data;
  };

  const completedSLPListExportSuccessCallback = (
    data: CompletedSLPListSuccessPayload
  ) => {
    if (!data.completedSLPs.length) {
      toast.error(NO_RECORD_FOUND);

      return;
    }

    const modifiedData = getSLPRequestsModifiedData(data.completedSLPs);
    const csvFileHeaders = [
      "SLP Number",
      "Student Name",
      "SLP Status",
      "Date of Request",
      "Date Completed",
      "Allocated To",
    ];

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvFileHeaders,
      filename: "CompletedSLPs",
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(modifiedData);
  };

  const getExportCompletedSLPList = () => {
    const { completedSLPListRequest } = props;
    if (completedSLPListRequest) {
      showLoader();
      const values: ISLPRequestsPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        DEFAULT_PAGE_NO,
        MAX_INT_VALUE
      );
      values.search = searchText.trim();

      if (filteredData.allocatedTo)
        values.allocatedTo = filteredData.allocatedTo;
      if (filteredData.numberOfCompletedSLP)
        values.numberOfCompletedSLP = filteredData.numberOfCompletedSLP;
      if (filteredData.from)
        values.from = dayjs(filteredData.from).format(DATE_ONLY_FORMAT);
      if (filteredData.to)
        values.to = dayjs(filteredData.to).format(DATE_ONLY_FORMAT);
      if (filteredData.slpParticipation)
        values.slpParticipation = filteredData.slpParticipation;

      const payload = {
        values,
        callback: completedSLPListExportSuccessCallback,
      };

      completedSLPListRequest(payload);
      handleExportClose();
    }
  };

  useEffect(() => {
    if (!appliedFilter) getSLPRequests(filteredData, true, DEFAULT_PAGE_NO);
    else getSLPRequests(filteredData);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [page, pageNo, order, orderBy]);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof ISLPCompletedKeys) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const handleStudentSearchKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (allowSearch(e)) {
      if (pageNo === DEFAULT_PAGE_NO) {
        getSLPRequests(filteredData);
      } else setPageNo(DEFAULT_PAGE_NO);
    }
    onTextFieldKeyPress(e, searchText?.length);
  };

  const handleStudentSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    if (pageNo === DEFAULT_PAGE_NO) {
      getSLPRequests(filteredData, true);
    } else setPageNo(DEFAULT_PAGE_NO);
  };

  const handleFilterApplyClick = (formData: ISLPRequestsFilter) => {
    if (
      formData.from &&
      formData.to &&
      checkEndDateLessThanStart(new Date(formData.from), new Date(formData.to))
    ) {
      toast.error(COMPLETED_TO_DATE_SHOULD_GREATER_MSG);

      return;
    }

    setFilteredData(formData);
    setSearchText("");
    if (pageNo === DEFAULT_PAGE_NO) {
      getSLPRequests(formData, true, DEFAULT_PAGE_NO);
    } else {
      setPageNo(DEFAULT_PAGE_NO);
    }
    setFilterBox(false);
    toggleFilter();
  };

  const completedSLPListPDFSuccessCallback = (
    response: CompletedSLPListPDFSuccessPayload
  ) => {
    printPDFHelper(response.completedSLPsPDF);
  };

  const getCompletedSLPListPDF = () => {
    const { completedSLPListPDFRequest } = props;
    if (completedSLPListPDFRequest) {
      showLoader();
      const values: ISLPRequestsPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        DEFAULT_PAGE_NO,
        MAX_INT_VALUE
      );
      values.search = searchText.trim();

      if (filteredData.allocatedTo)
        values.allocatedTo = filteredData.allocatedTo;
      if (filteredData.numberOfCompletedSLP)
        values.numberOfCompletedSLP = filteredData.numberOfCompletedSLP;
      if (filteredData.from)
        values.from = dayjs(filteredData.from).format(DATE_ONLY_FORMAT);
      if (filteredData.to)
        values.to = dayjs(filteredData.to).format(DATE_ONLY_FORMAT);
      if (filteredData.slpParticipation)
        values.slpParticipation = filteredData.slpParticipation;

      const payload = {
        values,
        callback: completedSLPListPDFSuccessCallback,
      };

      completedSLPListPDFRequest(payload);
      handleExportClose();
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Card>
          <Box className="table-card-header">
            <Typography variant="h3">Completed SLPs</Typography>
            <Box>
              <TextField
                id="search-box"
                variant="outlined"
                className="search-input"
                placeholder="Search Student"
                value={searchText}
                onChange={handleStudentSearch}
                onKeyDown={handleStudentSearchKeyDown}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      sx={{ position: "absolute", right: "0" }}
                      position="end"
                    >
                      {searchText && (
                        <Tooltip
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                          title={CLEAR_TOOLTIP_TITLE}
                          arrow
                        >
                          <IconButton edge="end" onClick={handleClearSearch}>
                            <img src={closeIcon} alt="close" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                id="export-btn"
                aria-controls={exportOpen ? "export-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={exportOpen ? "true" : undefined}
                onClick={handleExportClick}
                disableElevation
                variant="outlined"
                className="btn-export"
                disabled={!completedSLPsMeta?.totalCount}
              >
                Export
              </Button>
              <Menu
                id="export-menu"
                anchorEl={exportMenu}
                open={exportOpen}
                onClose={handleExportClose}
                MenuListProps={{
                  "aria-labelledby": "export-button",
                }}
                className="export-menu"
              >
                <MenuItem title="Excel" onClick={getExportCompletedSLPList}>
                  <Button>Excel</Button>
                </MenuItem>
                <MenuItem title="PDF" onClick={getCompletedSLPListPDF}>
                  <Button>PDF</Button>
                </MenuItem>
              </Menu>

              <Button
                variant="outlined"
                className="btn-filter"
                onClick={() => {
                  setFilterBox(true);
                  toggleFilter();
                }}
              >
                <img src={filterPrimaryIcon} alt="filter" className="default" />
                <img src={filterLightIcon} alt="filter" className="active" />
                <span>Filter</span>
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      maxWidth: "70px",
                      width: "70px",
                      minWidth: "70px",
                    }}
                  >
                    SLP Number
                  </TableCell>
                  <EnhancedTableHead
                    onRequestSort={(e) => handleRequestSort(e, "studentName")}
                    order={order}
                    orderBy={orderBy}
                    fieldName="Student Name"
                    keyName="studentName"
                    sxStyle={{ width: "130px", minWidth: "130px" }}
                  />
                  <TableCell sx={{ width: "90px" }} align="center">
                    SLP Status
                  </TableCell>
                  <EnhancedTableHead
                    onRequestSort={(e) => handleRequestSort(e, "dateOfRequest")}
                    order={order}
                    orderBy={orderBy}
                    fieldName="Date of Request"
                    keyName="dateOfRequest"
                    sxStyle={{ width: "140px", minWidth: "140px" }}
                  />
                  <EnhancedTableHead
                    onRequestSort={(e) => handleRequestSort(e, "dateCompleted")}
                    order={order}
                    orderBy={orderBy}
                    fieldName="Date Completed"
                    keyName="dateCompleted"
                    sxStyle={{ width: "140px", minWidth: "140px" }}
                  />
                  <TableCell sx={{ width: "130px", minWidth: "130px" }}>
                    Allocated To
                  </TableCell>
                  <TableCell align="center" sx={{ width: "92px" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedSLPs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell component="th" scope="row" align="center">
                      {row.slpNumber}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.studentName}</Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      className={`${getSLPStatusClass(row.status)} status`}
                    >
                      <div>{getSLPStatusText(row.status)}</div>
                    </TableCell>
                    <TableCell>
                      {row.dateOfRequest
                        ? moment(row.dateOfRequest).format(DATE_VIEW_FORMAT)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.dateCompleted
                        ? moment(row.dateCompleted).format(DATE_VIEW_FORMAT)
                        : "-"}
                    </TableCell>
                    <TableCell>{row.allocatedToName ?? "-"}</TableCell>
                    <TableCell align="center">
                      <div className="table-actions">
                        <Tooltip title="View SLP" arrow>
                          <IconButton
                            onClick={() =>
                              history.push({
                                pathname: AppRoutings.SLPView,
                                state: {
                                  studentId: Number(row.studentId),
                                  slpId: Number(row.id),
                                },
                              })
                            }
                          >
                            <img
                              src={courseListSecondaryIcon}
                              alt="View"
                              style={{ height: "17px", width: "17px" }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit SLP" arrow>
                          <IconButton
                            onClick={() =>
                              history.push({
                                pathname: AppRoutings.SLPForm,
                                state: {
                                  studentId: Number(row.studentId),
                                  slpId: Number(row.id),
                                  partNumber: SLPFormStep.HOPES_GOALS_AMBITIONS,
                                  isCompletedSLP: true,
                                },
                              })
                            }
                          >
                            <img src={listCheckIcon} alt="Edit" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {completedSLPsMeta && !!completedSLPs.length ? (
            <CustomPagination
              handleChange={handleChange}
              page={page}
              pageNo={pageNo}
              onPageChange={onPageChange}
              listMeta={completedSLPsMeta}
            />
          ) : (
            <NoRecords />
          )}
        </Card>
      </Grid>
      {filterBox && (
        <SLPRequestsFilter
          key="slp-completed-filter"
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setFilteredData={setFilteredData}
          getSLPRequests={getSLPRequests}
          setAppliedFilter={setAppliedFilter}
          plaUserListRequest={plaUserListRequest}
          isForCompletedSLP
        />
      )}
    </>
  );
};
export default CompletedSLPs;
